// src/pages/BracketPage.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";
import { FaTrophy, FaCalendar, FaUsers, FaDiscord, FaGamepad, FaMedal, FaArrowRight } from "react-icons/fa";
import DraftWarsImg from "../assets/draftwars/draftwars.png";
import TwitchEmbed from "../components/TwitchEmbed";
import { subscribeToStreamSettings } from "../lib/siteSettings";

// ── Format helpers ────────────────────────────────────────────────────────────
const isEliminationFormat = (f) => !["warzone_trios", "warzone_duos"].includes(f || "1v1");
const isCDLFormat = (f) => ["cdl_3v3", "cdl_4v4"].includes(f || "");
const parseParticipant = (str) => {
  if (!str) return { teamName: "", roster: [] };
  const [teamName, rosterStr] = str.split("|");
  return { teamName: teamName || str, roster: rosterStr ? rosterStr.split(",").filter(Boolean) : [] };
};

// ── Bracket layout constants ──────────────────────────────────────────────────
const CARD_H = 68;
const BASE_GAP = 8;
const CELL = CARD_H + BASE_GAP;
const CONNECTOR_W = 28;

function bracketGap(r)    { return Math.pow(2, r - 1) * CELL - CARD_H; }
function bracketPadTop(r) { return (Math.pow(2, r - 1) - 1) * CELL / 2; }
function armHeight(r)     { return Math.pow(2, r - 1) * CELL / 2; }

// ── Round label pill ──────────────────────────────────────────────────────────
function RoundLabel({ text }) {
  return (
    <div className="text-center mb-3">
      <span className="text-xs font-bold uppercase tracking-widest text-red-500 border border-red-600/30 bg-red-600/10 px-3 py-1 rounded-full whitespace-nowrap">
        {text}
      </span>
    </div>
  );
}

// ── Match card ────────────────────────────────────────────────────────────────
function MatchCard({ match, matchIndex, roundIndex, isFinalRound, isPlayIn, formatType }) {
  const t1Won = match.winner === match.team1Name && match.winner !== "" && match.team1Name !== "";
  const t2Won = match.winner === match.team2Name && match.winner !== "" && match.team2Name !== "";
  const isLive = match.status === "live";
  const isTop = matchIndex % 2 === 0;
  const arm = armHeight(roundIndex);
  const rowH = Math.floor((CARD_H - 1) / 2);
  const connectorColor = "rgb(75,85,99)";
  const cdl = isCDLFormat(formatType);

  return (
    <div className="relative flex-shrink-0" style={{ height: `${CARD_H}px`, width: "192px" }}>
      <div className={`h-full border rounded-lg overflow-hidden transition-all duration-200 ${
        isLive ? "border-green-600/60 bg-gray-900" : "border-gray-700 bg-gray-900 hover:border-red-600/40"
      }`}>
        {isLive && <div className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse z-10" />}

        {/* Team 1 row */}
        <div className={`flex items-center justify-between px-2.5 border-b border-gray-800 ${t1Won ? "bg-red-900/20" : ""}`} style={{ height: `${rowH}px` }}>
          <div className="flex-1 min-w-0">
            <span className={`text-xs font-semibold truncate block leading-none ${t1Won ? "text-white" : "text-gray-400"}`}>
              {match.team1Name || "TBD"}
            </span>
            {cdl && match.team1Roster?.length > 0 && (
              <span className="text-[9px] text-gray-600 truncate block leading-none mt-0.5">{match.team1Roster.join(", ")}</span>
            )}
          </div>
          <span className={`text-xs font-black ml-2 tabular-nums w-5 text-right flex-shrink-0 ${t1Won ? "text-red-400" : "text-gray-500"}`}>
            {match.status === "pending" ? "—" : (match.team1Score ?? "—")}
          </span>
        </div>

        {/* Team 2 row */}
        <div className={`flex items-center justify-between px-2.5 ${t2Won ? "bg-red-900/20" : ""}`} style={{ height: `${rowH}px` }}>
          <div className="flex-1 min-w-0">
            <span className={`text-xs font-semibold truncate block leading-none ${t2Won ? "text-white" : "text-gray-400"}`}>
              {match.team2Name || "TBD"}
            </span>
            {cdl && match.team2Roster?.length > 0 && (
              <span className="text-[9px] text-gray-600 truncate block leading-none mt-0.5">{match.team2Roster.join(", ")}</span>
            )}
          </div>
          <span className={`text-xs font-black ml-2 tabular-nums w-5 text-right flex-shrink-0 ${t2Won ? "text-red-400" : "text-gray-500"}`}>
            {match.status === "pending" ? "—" : (match.team2Score ?? "—")}
          </span>
        </div>
      </div>

      {/* Connector */}
      {!isFinalRound && (
        isPlayIn ? (
          <div style={{ position: "absolute", right: -CONNECTOR_W, top: "50%", width: CONNECTOR_W, height: 1, background: connectorColor }} />
        ) : (
          <div style={{
            position: "absolute", right: -CONNECTOR_W, width: CONNECTOR_W, height: arm,
            borderRight: `1px solid ${connectorColor}`,
            ...(isTop ? { top: "50%", borderBottom: `1px solid ${connectorColor}` } : { bottom: "50%", borderTop: `1px solid ${connectorColor}` }),
          }} />
        )
      )}
    </div>
  );
}

// ── Round label helper ────────────────────────────────────────────────────────
function getRoundLabel(round, matchCount) {
  if (round === 0) return "Play-In";
  if (matchCount === 1) return "Final";
  if (matchCount === 2) return "Semifinals";
  if (matchCount === 4) return "Quarterfinals";
  return `Round ${round}`;
}

// ── Warzone Leaderboard ───────────────────────────────────────────────────────
function WarzoneLeaderboard({ rounds }) {
  const [view, setView] = useState("all");

  // Compute cumulative totals
  const teamTotals = {};
  rounds.forEach((round) => {
    (round.standings || []).forEach((entry) => {
      if (!teamTotals[entry.teamName]) {
        teamTotals[entry.teamName] = { teamName: entry.teamName, roster: entry.roster || [], kills: 0, points: 0 };
      }
      teamTotals[entry.teamName].kills += entry.kills || 0;
      teamTotals[entry.teamName].points += entry.points || 0;
    });
  });
  const totalRows = Object.values(teamTotals).sort((a, b) => b.points - a.points);

  const selectedRound = rounds.find((r) => r.roundNumber === view);
  const displayRows = view === "all"
    ? totalRows
    : [...(selectedRound?.standings || [])].sort((a, b) => a.placement - b.placement);

  if (rounds.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <FaTrophy className="mx-auto mb-4 text-red-600/20" size={48} />
        <p className="text-lg">Standings will appear here once the tournament begins.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Round tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setView("all")}
          className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide transition-colors ${view === "all" ? "bg-red-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}
        >
          Total
        </button>
        {rounds.map((r) => (
          <button
            key={r.id}
            onClick={() => setView(r.roundNumber)}
            className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide transition-colors ${view === r.roundNumber ? "bg-red-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}
          >
            Round {r.roundNumber}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-500 w-12">#</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-500">Team</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-500 hidden sm:table-cell">Players</th>
              {view !== "all" && <th className="text-center py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-500">Place</th>}
              <th className="text-center py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-500">Kills</th>
              <th className="text-center py-3 px-4 text-xs font-bold uppercase tracking-widest text-red-500">Points</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => {
              const rank = view === "all" ? i + 1 : row.placement;
              const isFirst = rank === 1;
              return (
                <tr key={row.teamName} className={`border-b border-gray-800/60 transition-colors hover:bg-gray-900/50 ${isFirst ? "bg-red-900/10" : ""}`}>
                  <td className="py-3 px-4">
                    {isFirst
                      ? <FaMedal className="text-yellow-500" />
                      : <span className="text-gray-500 font-bold tabular-nums">{rank}</span>
                    }
                  </td>
                  <td className="py-3 px-4">
                    <span className={`font-bold ${isFirst ? "text-white" : "text-gray-300"}`}>{row.teamName}</span>
                  </td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    <span className="text-gray-500 text-xs">{(row.roster || []).join(", ") || "—"}</span>
                  </td>
                  {view !== "all" && (
                    <td className="py-3 px-4 text-center">
                      <span className="text-gray-300 tabular-nums">{row.placement ?? "—"}</span>
                    </td>
                  )}
                  <td className="py-3 px-4 text-center">
                    <span className="text-gray-300 tabular-nums">{row.kills ?? 0}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`font-black tabular-nums ${isFirst ? "text-red-400" : "text-white"}`}>{row.points ?? 0}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-600 text-center">Points: 1st=12 · 2nd=9 · 3rd=7 · 4th=5 · 5th=4 · 6th=3 · 7th=2 · 8th=1 · + kills</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function BracketPage() {
  const [tournament, setTournament] = useState(null);
  const [matchesByRound, setMatchesByRound] = useState({});
  const [allMatches, setAllMatches] = useState([]);
  const [warzoneRounds, setWarzoneRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("participants");
  const [draftWars, setDraftWars] = useState(null);
  const [stream, setStream] = useState(null);

  // Live stream banner — toggled by the team from the admin panel
  useEffect(() => subscribeToStreamSettings(setStream), []);

  // Listen for active Draft Wars session (shows live banner if active)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "draftWars", "session"), (snap) => {
      setDraftWars(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, []);

  const formatType = tournament?.formatType || "1v1";
  const eliminationType = tournament?.eliminationType || "single";
  const isElimination = isEliminationFormat(formatType);
  const isDoubleElim = isElimination && eliminationType === "double";

  // Listen for active tournament
  useEffect(() => {
    const q = query(collection(db, "tournaments"), where("isActive", "==", true));
    const unsub = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) { setTournament(null); setLoading(false); return; }
      const docSnap = snapshot.docs[0];
      setTournament({ id: docSnap.id, ...docSnap.data() });
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // Listen for matches (elimination formats)
  useEffect(() => {
    if (!tournament || !isEliminationFormat(tournament.formatType || "1v1")) {
      setMatchesByRound({});
      return;
    }
    const q = query(collection(db, "tournaments", tournament.id, "matches"));
    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllMatches(list);
      const grouped = {};
      // For double elim only group WB matches in the legacy round-grouping (used by single-elim renderer)
      const eliminationType = tournament.eliminationType || "single";
      list.forEach((match) => {
        if (eliminationType === "double" && match.bracket && match.bracket !== "WB") return;
        if (!grouped[match.round]) grouped[match.round] = [];
        grouped[match.round].push(match);
      });
      Object.keys(grouped).forEach((r) => {
        grouped[r].sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0));
      });
      setMatchesByRound(grouped);
      setLoading(false);
    });
    return () => unsub();
  }, [tournament]);

  // Listen for warzone rounds
  useEffect(() => {
    if (!tournament || isEliminationFormat(tournament.formatType || "1v1")) {
      setWarzoneRounds([]);
      if (tournament) setLoading(false);
      return;
    }
    const q = query(collection(db, "tournaments", tournament.id, "rounds"));
    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => a.roundNumber - b.roundNumber);
      setWarzoneRounds(list);
      setLoading(false);
    });
    return () => unsub();
  }, [tournament]);

  const roundNumbers = Object.keys(matchesByRound).map(Number).sort((a, b) => a - b);
  const participants = tournament?.participants || [];
  const playInMatches = matchesByRound[0] || [];
  const regularRounds = roundNumbers.filter((r) => r >= 1);
  const r1Count = matchesByRound[1]?.length ?? 0;

  // ── Double-elim grouping: { WB: { round: [matches] }, LB: ..., GF: ... } ──
  const matchesByBracket = (allMatches || []).reduce((acc, m) => {
    const b = m.bracket || "WB";
    if (!acc[b]) acc[b] = {};
    if (!acc[b][m.round]) acc[b][m.round] = [];
    acc[b][m.round].push(m);
    return acc;
  }, {});
  Object.keys(matchesByBracket).forEach((b) => {
    Object.keys(matchesByBracket[b]).forEach((r) => {
      matchesByBracket[b][r].sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0));
    });
  });

  const formatLabel = {
    "1v1": "1v1",
    "cdl_3v3": "CDL 3v3",
    "cdl_4v4": "CDL 4v4",
    "warzone_trios": "Warzone Trios",
    "warzone_duos": "Warzone Duos",
  }[formatType] || formatType;

  const draftWarsActive = draftWars && draftWars.status === "active";

  return (
    <main className="bg-black min-h-screen">

      {/* ── Hero ── */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black" />
        <div className="max-w-6xl mx-auto relative z-10">
          <p className="text-red-600 uppercase tracking-widest text-sm font-semibold mb-3">Prime Valor</p>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            <span className="text-red-600">Events</span>
          </h1>
          {tournament ? (
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-2"><FaTrophy className="text-red-600" />{tournament.title}</span>
              {tournament.date && <span className="flex items-center gap-2"><FaCalendar className="text-red-600" />{tournament.date}</span>}
              <span className="flex items-center gap-2"><FaUsers className="text-red-600" />{formatLabel}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                tournament.status === "live"
                  ? "bg-green-600/20 border-green-600 text-green-400"
                  : tournament.status === "completed"
                  ? "bg-gray-700 border-gray-600 text-gray-300"
                  : "bg-red-600/20 border-red-600 text-red-400"
              }`}>
                {tournament.status === "live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse mr-1.5 align-middle" />}
                {tournament.status}
              </span>
            </div>
          ) : (
            <p className="text-gray-400 text-lg max-w-xl">
              Follow live tournament results, brackets, and events in real time.
            </p>
          )}
        </div>
      </section>

      {/* ── Live stream ── */}
      {stream?.enabled && stream.channel && (
        <TwitchEmbed
          channel={stream.channel}
          title={stream.title}
          subtitle={stream.subtitle}
          showChat={stream.showChat !== false}
        />
      )}

      {/* ── Draft Wars LIVE banner ── */}
      {draftWarsActive && (
        <section className="px-6 pb-6">
          <div className="max-w-6xl mx-auto">
            <Link to="/draft-wars"
              className="group relative overflow-hidden block bg-gradient-to-r from-red-950 via-red-900 to-black border-2 border-red-600/60 rounded-2xl p-6 md:p-8 hover:border-red-500 transition-all hover:shadow-2xl hover:shadow-red-500/30">
              <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle at 20% 50%, rgba(220,38,38,0.3) 0%, transparent 50%),
                                  radial-gradient(circle at 80% 50%, rgba(220,38,38,0.3) 0%, transparent 50%)`
              }} />
              <div className="relative flex items-center gap-4">
                <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-black border-2 border-red-600/50 shadow-lg shadow-red-600/50">
                  <img src={DraftWarsImg} alt="Draft Wars" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                    </span>
                    <span className="text-xs font-bold uppercase tracking-widest text-red-400">Auction in progress</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl font-black uppercase text-white leading-tight">
                    Draft <span className="text-red-500">Wars</span>
                  </h2>
                  <p className="text-sm text-gray-400 mt-1">8 captains · 24 players · one champion. Click to watch live.</p>
                </div>
                <div className="flex-shrink-0 hidden sm:flex items-center gap-2 text-white font-bold uppercase tracking-wide text-sm group-hover:translate-x-1 transition-transform">
                  Watch <FaArrowRight />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ── Content ── */}
      <section className="py-8 px-6 pb-24">
        <div className="max-w-7xl mx-auto">

          {/* Section header for bracket */}
          {tournament && draftWarsActive && (
            <h2 className="text-lg font-black uppercase tracking-widest text-gray-400 mb-6 border-l-4 border-red-600 pl-3">
              Tournament Bracket
            </h2>
          )}

          {loading && (
            <div className="text-center py-24">
              <div className="text-gray-500 text-lg animate-pulse">Loading...</div>
            </div>
          )}

          {!loading && !tournament && !draftWarsActive && (
            <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 max-w-md w-full">
                <FaTrophy className="text-red-600/30 mx-auto mb-6" size={72} />
                <h2 className="text-2xl font-bold text-white mb-3">No Active Events</h2>
                <p className="text-gray-400 mb-8 leading-relaxed">
                  The next event will appear here when it goes live. Join our Discord to get notified first.
                </p>
                <a href="https://discord.gg/SGXXCMpCxf" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
                  <FaDiscord /> Join Discord
                </a>
              </div>
            </div>
          )}

          {!loading && tournament && (
            <>
              {/* Tabs */}
              <div className="flex gap-1 mb-8 border-b border-gray-800">
                <button
                  onClick={() => setActiveTab("participants")}
                  className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${activeTab === "participants" ? "border-red-600 text-red-500" : "border-transparent text-gray-400 hover:text-white"}`}
                >
                  <FaUsers className="inline mr-2" />
                  {isElimination ? `Participants (${participants.length})` : `Teams (${participants.length})`}
                </button>
                <button
                  onClick={() => setActiveTab(isElimination ? "bracket" : "standings")}
                  className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                    (activeTab === "bracket" || activeTab === "standings") ? "border-red-600 text-red-500" : "border-transparent text-gray-400 hover:text-white"
                  }`}
                >
                  <FaTrophy className="inline mr-2" />
                  {isElimination ? "Bracket" : "Standings"}
                </button>
              </div>

              {/* ── Participants tab ── */}
              {activeTab === "participants" && (
                <div>
                  {participants.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                      <FaGamepad className="mx-auto mb-4 text-red-600/20" size={48} />
                      <p className="text-lg">No participants registered yet.</p>
                      <p className="text-sm mt-2">Check back soon or join our Discord to sign up.</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-400 text-sm mb-6">
                        {participants.length} {isElimination && !isCDLFormat(formatType) ? "player" : "team"}{participants.length !== 1 ? "s" : ""} registered for{" "}
                        <span className="text-white font-semibold">{tournament.title}</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {participants.map((p, i) => {
                          const parsed = parseParticipant(p);
                          return (
                            <div key={i} className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 hover:border-red-600/40 transition-colors">
                              <div className="flex items-start gap-3">
                                <span className="text-xs font-bold text-red-600 tabular-nums w-5 flex-shrink-0 mt-0.5">{i + 1}</span>
                                <div className="min-w-0">
                                  <span className="text-white text-sm font-semibold block truncate">{parsed.teamName}</span>
                                  {parsed.roster.length > 0 && (
                                    <span className="text-gray-500 text-xs block mt-0.5">{parsed.roster.join(", ")}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Bracket tab (DOUBLE ELIMINATION) ── */}
              {activeTab === "bracket" && isElimination && isDoubleElim && (
                <>
                  {(allMatches || []).length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                      <FaTrophy className="mx-auto mb-4 text-red-600/20" size={48} />
                      <p className="text-lg">Bracket is being set up — check back shortly.</p>
                    </div>
                  ) : (
                    <div className="space-y-10">
                      {/* WINNERS BRACKET */}
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-sm font-black uppercase tracking-widest text-amber-400 border border-amber-600/40 bg-amber-600/10 px-4 py-1.5 rounded-full">
                            Winners Bracket
                          </span>
                          <div className="flex-1 h-px bg-gradient-to-r from-amber-600/40 to-transparent" />
                        </div>
                        <div className="overflow-x-auto pb-4">
                          <div className="flex items-start min-w-max" style={{ gap: `${CONNECTOR_W}px` }}>
                            {Object.keys(matchesByBracket.WB || {}).map(Number).sort((a, b) => a - b).map((round, colIdx, rounds) => {
                              const isFinalRound = colIdx === rounds.length - 1;
                              const padTop = bracketPadTop(round);
                              const gap = bracketGap(round);
                              const matchesInRound = matchesByBracket.WB[round];
                              const lbl = matchesInRound[0]?.label || (isFinalRound ? "WB Final" : `WB Round ${round}`);
                              return (
                                <div key={"wb-" + round} className="flex-shrink-0">
                                  <RoundLabel text={lbl} />
                                  <div style={{ paddingTop: `${padTop}px` }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: `${gap}px` }}>
                                      {matchesInRound.map((match, matchIndex) => (
                                        <MatchCard key={match.id} match={match} matchIndex={matchIndex} roundIndex={round} isFinalRound={isFinalRound} isPlayIn={false} formatType={formatType} />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* LOSERS BRACKET */}
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-sm font-black uppercase tracking-widest text-blue-400 border border-blue-600/40 bg-blue-600/10 px-4 py-1.5 rounded-full">
                            Losers Bracket
                          </span>
                          <div className="flex-1 h-px bg-gradient-to-r from-blue-600/40 to-transparent" />
                        </div>
                        <div className="overflow-x-auto pb-4">
                          <div className="flex items-start min-w-max" style={{ gap: `${CONNECTOR_W}px` }}>
                            {Object.keys(matchesByBracket.LB || {}).map(Number).sort((a, b) => a - b).map((round, colIdx, rounds) => {
                              const isFinalRound = colIdx === rounds.length - 1;
                              const matchesInRound = matchesByBracket.LB[round];
                              const lbl = matchesInRound[0]?.label || `LB Round ${round}`;
                              return (
                                <div key={"lb-" + round} className="flex-shrink-0">
                                  <RoundLabel text={lbl} />
                                  <div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: `${BASE_GAP * 2}px` }}>
                                      {matchesInRound.map((match, matchIndex) => (
                                        <MatchCard key={match.id} match={match} matchIndex={matchIndex} roundIndex={1} isFinalRound={isFinalRound} isPlayIn={false} formatType={formatType} />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* GRAND FINAL */}
                      {matchesByBracket.GF && (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-sm font-black uppercase tracking-widest text-red-400 border border-red-600/50 bg-red-600/10 px-4 py-1.5 rounded-full">
                              Grand Final
                            </span>
                            <div className="flex-1 h-px bg-gradient-to-r from-red-600/40 to-transparent" />
                          </div>
                          <div className="flex justify-start sm:justify-center">
                            {Object.values(matchesByBracket.GF).flat().map((match, idx) => (
                              <MatchCard key={match.id} match={match} matchIndex={idx} roundIndex={1} isFinalRound={true} isPlayIn={false} formatType={formatType} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Bracket tab (SINGLE ELIMINATION) ── */}
              {activeTab === "bracket" && isElimination && !isDoubleElim && (
                <>
                  {roundNumbers.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                      <FaTrophy className="mx-auto mb-4 text-red-600/20" size={48} />
                      <p className="text-lg">Bracket is being set up — check back shortly.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto pb-8">
                      <div className="flex items-start min-w-max" style={{ gap: `${CONNECTOR_W}px` }}>
                        {/* Play-In column */}
                        {playInMatches.length > 0 && (
                          <div className="flex-shrink-0">
                            <RoundLabel text="Play-In" />
                            <div style={{ paddingTop: `${(r1Count - 1) * CELL}px` }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: `${BASE_GAP}px` }}>
                                {playInMatches.map((match, idx) => (
                                  <MatchCard key={match.id} match={match} matchIndex={idx} roundIndex={1} isFinalRound={regularRounds.length === 0} isPlayIn={true} formatType={formatType} />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Regular rounds */}
                        {regularRounds.map((round, colIdx) => {
                          const isFinalRound = colIdx === regularRounds.length - 1;
                          const padTop = bracketPadTop(round);
                          const gap = bracketGap(round);
                          const label = getRoundLabel(round, matchesByRound[round].length);
                          return (
                            <div key={round} className="flex-shrink-0">
                              <RoundLabel text={label} />
                              <div style={{ paddingTop: `${padTop}px` }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: `${gap}px` }}>
                                  {matchesByRound[round].map((match, matchIndex) => (
                                    <MatchCard key={match.id} match={match} matchIndex={matchIndex} roundIndex={round} isFinalRound={isFinalRound} isPlayIn={false} formatType={formatType} />
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Standings tab (Warzone) ── */}
              {activeTab === "standings" && !isElimination && (
                <WarzoneLeaderboard rounds={warzoneRounds} />
              )}
            </>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      {!loading && (
        <section className="py-16 px-6 bg-gradient-to-r from-red-900/20 to-gray-900 border-t border-gray-800">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Stay in the Loop</h2>
            <p className="text-gray-400 mb-8">Get real-time match notifications and tournament updates on our Discord.</p>
            <a href="https://discord.gg/SGXXCMpCxf" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
              <FaDiscord /> Join Discord
            </a>
          </div>
        </section>
      )}
    </main>
  );
}

export default BracketPage;
